import { isAxiosError } from 'axios';

const CODE_MESSAGES: Record<string, string> = {
  location_required: 'Select a location before saving these settings.',
  form_locked: 'This form is locked — fork it to make changes.',
  open_assignments: 'This form has open assignments — fork it to edit.',
  version_conflict: 'Form was updated elsewhere — refreshing latest version.',
  dob_mismatch: 'Date of birth does not match our records.',
  dob_locked: 'Too many attempts — try again later.',
  token_inactive: 'This form link is no longer active.',
  assignment_closed: 'This form assignment is closed.',
  required_consent_non_removable: 'Required consent blocks cannot be removed from intake forms.',
};

function firstFieldError(data: Record<string, unknown>): string | undefined {
  for (const value of Object.values(data)) {
    if (typeof value === 'string') return value;
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
      return value[0];
    }
  }
  return undefined;
}

/**
 * Normalize any thrown value (typically an Axios error) into a human-readable
 * message, preferring the backend's `detail`/`error`/field messages.
 */
export function extractApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | string | undefined;

    if (typeof data === 'string' && data.trim()) return data;

    if (data && typeof data === 'object') {
      const code = typeof data.error === 'string' ? data.error : undefined;
      const detail = typeof data.detail === 'string' ? data.detail : undefined;
      const message = typeof data.message === 'string' ? data.message : undefined;

      if (code && CODE_MESSAGES[code]) return CODE_MESSAGES[code];
      if (detail) return detail;
      if (message) return message;
      if (code) return code;

      const field = firstFieldError(data);
      if (field) return field;
    }

    return error.message || 'Request failed';
  }

  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}

/** HTTP status of an Axios error, or null for non-HTTP failures. */
export function errorStatus(error: unknown): number | null {
  if (isAxiosError(error)) return error.response?.status ?? null;
  return null;
}

/** Statuses we surface to the user immediately and never retry. */
export function isClientError(error: unknown): boolean {
  const status = errorStatus(error);
  return status != null && status >= 400 && status < 500;
}
